import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  WxbAlert,
  WxbBadge,
  WxbButton,
  WxbDataTable,
  WxbFormField,
  WxbInput,
  WxbModal,
  WxbPageHeader,
  WxbPageSection,
  WxbPageShell,
  WxbSelect,
  WxbSpinner,
  WxbTableActionCell,
  WxbTag,
  WxbTextarea,
  WxbValidationList,
} from '../../components/wxb-ui';
import type { WxbTagColor } from '../../components/wxb-ui/Tag/Tag';
import { wxbToast } from '../../components/wxb-ui/Toast/Toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  platformSiteApi,
  type CreateSitePayload,
  type IsolationReadiness,
  type PlatformSite,
  type PlatformUser,
  type SiteLifecycleAction,
  type SiteLifecycleRequest,
  type SiteReadinessReport,
  type SiteStatus,
} from '../../services/platformSiteApi';
import './SiteManagementPage.css';

const STATUS_META: Record<SiteStatus, { label: string; color: WxbTagColor }> = {
  PROVISIONING: { label: '开通中', color: 'cyan' },
  INACTIVE: { label: '待启用', color: 'amber' },
  ACTIVE: { label: '运行中', color: 'green' },
  SUSPENDED: { label: '已暂停', color: 'red' },
  RETIRED: { label: '已退役', color: 'neutral' },
};

const ACTION_LABEL: Record<SiteLifecycleAction, string> = {
  ACTIVATE: '启用',
  RESUME: '恢复',
  RETIRE: '退役',
};

const FALLBACK_TIMEZONES = [
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Kolkata',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Australia/Sydney',
  'UTC',
];

const TIMEZONE_OPTIONS = (() => {
  const supportedValuesOf = (
    Intl as typeof Intl & {
      supportedValuesOf?: (key: 'timeZone') => string[];
    }
  ).supportedValuesOf;
  const values = supportedValuesOf
    ? supportedValuesOf('timeZone')
    : FALLBACK_TIMEZONES;
  const timezones = values.includes('Asia/Shanghai')
    ? values
    : ['Asia/Shanghai', ...values];
  return timezones.map((timezone) => ({
    value: timezone,
    label: timezone,
  }));
})();

type LifecycleDialogAction =
  | SiteLifecycleAction
  | 'SUSPEND'
  | 'TEST_ACTIVATE';

const createEmptyForm = (userId?: number): CreateSitePayload => ({
  siteCode: '',
  siteName: '',
  timezone: 'Asia/Shanghai',
  dataRegion: 'CN',
  templateVersion: 'v2',
  initialAdminUserId: userId ?? 0,
});

const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `site-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const errorMessage = (error: unknown, fallback: string): string => {
  const response = (error as {
    response?: { data?: { code?: string; error?: string } };
  })?.response?.data;
  switch (response?.code) {
    case 'MULTI_SITE_GATE_CLOSED':
      return '多 Site 隔离门禁尚未通过，当前禁止新建 Site';
    case 'SITE_CODE_EXISTS':
      return 'Site 编码已存在';
    case 'IDEMPOTENCY_CONFLICT':
      return '重复提交标识与原请求内容不一致，请关闭弹窗后重试';
    case 'SITE_NOT_READY':
      return 'Site 校验未通过，不能发起该生命周期操作';
    case 'MAKER_CHECKER_VIOLATION':
      return '申请人与审批人必须是不同用户';
    case 'READINESS_EVIDENCE_STALE':
      return '审批依据已发生变化，请重新校验并发起申请';
    default:
      return response?.error || fallback;
  }
};

const SiteManagementPage: React.FC = () => {
  const { user, hasPlatformPermission, refreshSession } = useAuth();
  const canCreate = hasPlatformPermission('PLATFORM_SITE_CREATE');
  const canConfigure = hasPlatformPermission('PLATFORM_SITE_CONFIGURE');
  const canValidate = hasPlatformPermission('PLATFORM_SITE_VALIDATE');
  const canActivate = hasPlatformPermission('PLATFORM_SITE_ACTIVATE');
  const canSuspend = hasPlatformPermission('PLATFORM_SITE_SUSPEND');
  const canRetire = hasPlatformPermission('PLATFORM_SITE_RETIRE');
  const canApprove = hasPlatformPermission('PLATFORM_SITE_READINESS_APPROVE');
  const canTestActivate = canActivate && canApprove;

  const [sites, setSites] = useState<PlatformSite[]>([]);
  const [requests, setRequests] = useState<SiteLifecycleRequest[]>([]);
  const [platformUsers, setPlatformUsers] = useState<PlatformUser[]>([]);
  const [readiness, setReadiness] = useState<IsolationReadiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateSitePayload>(
    createEmptyForm(user?.userId),
  );
  const [creating, setCreating] = useState(false);
  const [createIdempotencyKey, setCreateIdempotencyKey] = useState(
    newIdempotencyKey,
  );
  const [lifecycleTarget, setLifecycleTarget] = useState<{
    site: PlatformSite;
    action: LifecycleDialogAction;
  } | null>(null);
  const [lifecycleReason, setLifecycleReason] = useState('');
  const [lifecycleSaving, setLifecycleSaving] = useState(false);
  const [validationReport, setValidationReport] =
    useState<SiteReadinessReport | null>(null);
  const [validationOpen, setValidationOpen] = useState(false);
  const [validating, setValidating] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [siteData, requestData, readinessData] = await Promise.all([
        platformSiteApi.listSites(),
        platformSiteApi.listLifecycleRequests(),
        platformSiteApi.getReadiness(),
      ]);
      setSites(siteData);
      setRequests(requestData);
      setReadiness(readinessData);
      if (canConfigure) {
        setPlatformUsers(await platformSiteApi.listUsers());
      }
    } catch {
      setLoadError(true);
      wxbToast.error('加载 Site 控制面失败');
    } finally {
      setLoading(false);
    }
  }, [canConfigure]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreate = () => {
    setCreateForm(createEmptyForm(user?.userId));
    setCreateIdempotencyKey(newIdempotencyKey());
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (
      !createForm.siteCode.trim()
      || !createForm.siteName.trim()
      || !createForm.initialAdminUserId
    ) {
      wxbToast.error('请填写 Site 编码、名称并选择初始管理员');
      return;
    }
    setCreating(true);
    try {
      await platformSiteApi.createSite(createForm, createIdempotencyKey);
      wxbToast.success('Site 已完成开通，当前处于待启用状态');
      setCreateOpen(false);
      await loadData();
    } catch (error) {
      wxbToast.error(errorMessage(error, '新建 Site 失败'));
    } finally {
      setCreating(false);
    }
  };

  const runValidation = useCallback(async (
    site: PlatformSite,
    action: SiteLifecycleAction,
  ) => {
    setValidationOpen(true);
    setValidationReport(null);
    setValidating(true);
    try {
      setValidationReport(await platformSiteApi.validateSite(site.publicId, action));
    } catch (error) {
      wxbToast.error(errorMessage(error, 'Site 校验失败'));
      setValidationOpen(false);
    } finally {
      setValidating(false);
    }
  }, []);

  const submitLifecycle = async () => {
    if (!lifecycleTarget || !lifecycleReason.trim()) {
      wxbToast.error('请填写操作原因');
      return;
    }
    setLifecycleSaving(true);
    try {
      if (lifecycleTarget.action === 'SUSPEND') {
        await platformSiteApi.suspendSite(
          lifecycleTarget.site.publicId,
          lifecycleReason.trim(),
        );
        wxbToast.success('Site 已紧急暂停');
      } else if (lifecycleTarget.action === 'TEST_ACTIVATE') {
        await platformSiteApi.activateNonProductionTestSite(
          lifecycleTarget.site.publicId,
          lifecycleReason.trim(),
        );
        await refreshSession();
        wxbToast.success('测试 Site 已启用，可以从顶部 Site 切换器进入');
      } else {
        await platformSiteApi.requestLifecycle(
          lifecycleTarget.site.publicId,
          lifecycleTarget.action,
          lifecycleReason.trim(),
        );
        wxbToast.success('申请已提交，需由另一位平台管理员审批');
      }
      setLifecycleTarget(null);
      setLifecycleReason('');
      await loadData();
    } catch (error) {
      wxbToast.error(errorMessage(error, '生命周期操作失败'));
    } finally {
      setLifecycleSaving(false);
    }
  };

  const approveRequest = useCallback(async (request: SiteLifecycleRequest) => {
    try {
      await platformSiteApi.approveLifecycleRequest(request.publicId);
      wxbToast.success(`${ACTION_LABEL[request.action]}申请已审批并执行`);
      await loadData();
    } catch (error) {
      wxbToast.error(errorMessage(error, '审批失败'));
    }
  }, [loadData]);

  const siteColumns = useMemo(() => [
    {
      title: 'Site',
      key: 'site',
      render: (_: unknown, record: PlatformSite) => (
        <div className="gv-site-identity">
          <span className="gv-site-name">{record.siteName}</span>
          <WxbBadge variant="code" status="info" label={record.siteCode} />
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'siteStatus',
      key: 'siteStatus',
      render: (status: SiteStatus) => (
        <WxbTag color={STATUS_META[status].color}>{STATUS_META[status].label}</WxbTag>
      ),
    },
    {
      title: '时区 / 数据区域',
      key: 'location',
      render: (_: unknown, record: PlatformSite) => (
        <div className="gv-site-secondary">
          <span>{record.timezone}</span>
          <span>{record.dataRegion ?? '未配置'}</span>
        </div>
      ),
    },
    {
      title: '数据落位',
      key: 'placement',
      render: (_: unknown, record: PlatformSite) => record.placement ? (
        <div className="gv-site-secondary">
          <span>{record.placement.type === 'SHARED_DB' ? '共享数据库' : '独立数据库'}</span>
          <span>{record.placement.schemaVersion} · v{record.placement.version}</span>
        </div>
      ) : <WxbTag color="amber">未落位</WxbTag>,
    },
    {
      title: '开通任务',
      key: 'provisioning',
      render: (_: unknown, record: PlatformSite) => {
        if (!record.provisioning) return <span className="gv-site-muted">历史 Site</span>;
        const status = record.provisioning.status;
        return (
          <WxbTag color={status === 'SUCCEEDED' ? 'green' : status === 'FAILED' ? 'red' : 'cyan'}>
            {status === 'SUCCEEDED' ? '已完成' : status === 'FAILED' ? '失败' : '执行中'}
          </WxbTag>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 260,
      render: (_: unknown, record: PlatformSite) => {
        const actions = [];
        if (record.siteStatus === 'INACTIVE') {
          actions.push({
            key: 'validate',
            label: '校验',
            disabled: !canValidate,
            onClick: () => runValidation(record, 'ACTIVATE'),
          });
          if (readiness?.nonProductionTestMode) {
            actions.push({
              key: 'test-activate',
              label: '测试启用',
              disabled: !canTestActivate,
              onClick: () => {
                setLifecycleReason('');
                setLifecycleTarget({ site: record, action: 'TEST_ACTIVATE' });
              },
            });
          } else {
            actions.push({
              key: 'activate',
              label: '申请启用',
              disabled: !canActivate,
              onClick: () => {
                setLifecycleReason('');
                setLifecycleTarget({ site: record, action: 'ACTIVATE' });
              },
            });
          }
        }
        if (record.siteStatus === 'ACTIVE') {
          actions.push({
            key: 'suspend',
            label: '紧急暂停',
            variant: 'danger' as const,
            disabled: !canSuspend,
            onClick: () => {
              setLifecycleReason('');
              setLifecycleTarget({ site: record, action: 'SUSPEND' });
            },
          });
        }
        if (record.siteStatus === 'SUSPENDED') {
          actions.push({
            key: 'resume',
            label: '申请恢复',
            disabled: !canActivate,
            onClick: () => {
              setLifecycleReason('');
              setLifecycleTarget({ site: record, action: 'RESUME' });
            },
          });
          actions.push({
            key: 'retire',
            label: '申请退役',
            variant: 'danger' as const,
            disabled: !canRetire,
            onClick: () => {
              setLifecycleReason('');
              setLifecycleTarget({ site: record, action: 'RETIRE' });
            },
          });
        }
        return actions.length > 0
          ? <WxbTableActionCell actions={actions} maxInline={2} />
          : <span className="gv-site-muted">无可用操作</span>;
      },
    },
  ], [
    canActivate,
    canTestActivate,
    canRetire,
    canSuspend,
    canValidate,
    readiness?.nonProductionTestMode,
    runValidation,
  ]);

  const requestColumns = useMemo(() => [
    { title: 'Site', dataIndex: 'siteCode', key: 'siteCode' },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      render: (action: SiteLifecycleAction) => ACTION_LABEL[action],
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: SiteLifecycleRequest['status']) => (
        <WxbTag color={status === 'REQUESTED' ? 'amber' : status === 'EXECUTED' ? 'green' : 'neutral'}>
          {status === 'REQUESTED' ? '待审批' : status === 'EXECUTED' ? '已执行' : status}
        </WxbTag>
      ),
    },
    { title: '原因', dataIndex: 'reason', key: 'reason' },
    {
      title: '申请人',
      dataIndex: 'requestedBy',
      key: 'requestedBy',
      render: (value: number) => `用户 #${value}`,
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: unknown, record: SiteLifecycleRequest) => (
        <WxbTableActionCell
          actions={[{
            key: 'approve',
            label: '审批并执行',
            disabled:
              !canApprove
              || record.status !== 'REQUESTED'
              || record.requestedBy === user?.userId,
            onClick: () => approveRequest(record),
            confirm: {
              title: `确认审批并执行“${ACTION_LABEL[record.action]}”？`,
              description: '审批后将立即变更 Site 状态，申请人与审批人必须不同。',
            },
          }]}
          maxInline={1}
        />
      ),
    },
  ], [approveRequest, canApprove, user?.userId]);

  if (loading) {
    return (
      <WxbPageShell>
        <div className="gv-site-loading">
          <WxbSpinner tip="加载 Site 控制面…" />
        </div>
      </WxbPageShell>
    );
  }

  return (
    <WxbPageShell size="wide">
      <WxbPageHeader
        eyebrow="平台控制面"
        title="Site 管理"
        description="创建和管理彼此隔离的 Site。创建、启用、暂停与退役均通过平台权限执行，不继承当前 Site 的管理员权限。"
        meta={<WxbBadge variant="bar" status="info" label={`共 ${sites.length} 个 Site`} />}
        actions={canCreate ? (
          <WxbButton
            variant="primary"
            disabled={!readiness?.multiSiteOperationsAllowed}
            onClick={openCreate}
          >
            新建 Site
          </WxbButton>
        ) : undefined}
      />

      {readiness?.nonProductionTestMode ? (
        <WxbAlert title="非生产多 Site 测试模式已开启">
          你可以创建并测试启用额外 Site，然后从顶部 Site 切换器进入并录入测试数据。
          测试启用会写入安全审计，但不代表生产 readiness 已获批准；跨 Site
          借调与资质互认仍然关闭。
        </WxbAlert>
      ) : !readiness?.multiSiteEnforcementReady ? (
        <WxbAlert title="多 Site 隔离门禁尚未开启">
          当前仅允许 MFG8 运行。完成数据所有权迁移、接口覆盖审计和双 Site
          隔离测试后，平台审批人才能开启门禁；在此之前新建 Site 会被后端拒绝。
        </WxbAlert>
      ) : null}

      <WxbPageSection
        title="Site 目录"
        description="Site 编码和数据区域创建后不可修改；启用需要独立申请与审批。"
        actions={
          <WxbButton variant="secondary" onClick={loadData}>
            刷新
          </WxbButton>
        }
        variant="framed"
      >
        <WxbDataTable<PlatformSite>
          rowKey="publicId"
          columns={siteColumns}
          dataSource={sites}
          pagination={false}
          errorState={loadError ? {
            title: '加载失败',
            description: '无法读取 Site 目录',
          } : undefined}
          emptyState={{ description: '暂无 Site' }}
        />
      </WxbPageSection>

      <WxbPageSection
        title="生命周期审批"
        description="启用、恢复和退役实行申请人与审批人分离。紧急暂停立即生效并写入安全审计。"
        variant="framed"
      >
        <WxbDataTable<SiteLifecycleRequest>
          rowKey="publicId"
          columns={requestColumns}
          dataSource={requests}
          pagination={false}
          emptyState={{ description: '暂无生命周期申请' }}
        />
      </WxbPageSection>

      <WxbModal
        open={createOpen}
        title="新建 Site"
        okText="创建并开通"
        cancelText="取消"
        confirmLoading={creating}
        onCancel={() => setCreateOpen(false)}
        onOk={submitCreate}
        destroyOnClose
      >
        <div className="gv-site-form">
          <WxbInput
            label="Site 编码"
            value={createForm.siteCode}
            placeholder="例如 SITE_B"
            onChange={(event) => setCreateForm((current) => ({
              ...current,
              siteCode: event.target.value.toUpperCase(),
            }))}
          />
          <WxbInput
            label="Site 名称"
            value={createForm.siteName}
            onChange={(event) => setCreateForm((current) => ({
              ...current,
              siteName: event.target.value,
            }))}
          />
          <WxbSelect
            label="时区"
            value={createForm.timezone}
            options={TIMEZONE_OPTIONS}
            showSearch
            optionFilterProp="label"
            onChange={(value) => setCreateForm((current) => ({
              ...current,
              timezone: String(value),
            }))}
          />
          <WxbInput
            label="数据区域"
            value={createForm.dataRegion}
            disabled
            helpText="共享数据库模式下必须与当前部署区域一致"
          />
          <WxbInput
            label="角色模板版本"
            value={createForm.templateVersion}
            disabled
          />
          <WxbSelect
            label="初始 Site 管理员"
            value={createForm.initialAdminUserId || undefined}
            options={platformUsers.map((platformUser) => ({
              value: platformUser.id,
              label: `${platformUser.displayName} (@${platformUser.username})`,
            }))}
            onChange={(value) => setCreateForm((current) => ({
              ...current,
              initialAdminUserId: Number(value),
            }))}
          />
        </div>
      </WxbModal>

      <WxbModal
        open={Boolean(lifecycleTarget)}
        title={
          lifecycleTarget?.action === 'SUSPEND'
            ? '紧急暂停 Site'
            : lifecycleTarget?.action === 'TEST_ACTIVATE'
              ? '测试启用 Site'
            : `申请${lifecycleTarget ? ACTION_LABEL[lifecycleTarget.action] : ''} Site`
        }
        okText={
          lifecycleTarget?.action === 'SUSPEND'
            ? '立即暂停'
            : lifecycleTarget?.action === 'TEST_ACTIVATE'
              ? '确认测试启用'
              : '提交申请'
        }
        okVariant={lifecycleTarget?.action === 'SUSPEND' ? 'danger' : 'primary'}
        confirmLoading={lifecycleSaving}
        onCancel={() => setLifecycleTarget(null)}
        onOk={submitLifecycle}
        destroyOnClose
      >
        <WxbFormField
          label="操作原因"
          required
          helpText={
            lifecycleTarget?.action === 'SUSPEND'
              ? '紧急暂停会立即阻止该 Site 的业务访问。'
              : lifecycleTarget?.action === 'TEST_ACTIVATE'
                ? '仅用于本机/测试数据。该操作绕过生产 maker-checker，但会完整写入安全审计。'
              : '提交后必须由另一位平台管理员审批。'
          }
        >
          <WxbTextarea
            value={lifecycleReason}
            maxLength={500}
            rows={4}
            onChange={(event) => setLifecycleReason(event.target.value)}
          />
        </WxbFormField>
      </WxbModal>

      <WxbModal
        open={validationOpen}
        title="Site 启用校验"
        footer={null}
        onCancel={() => setValidationOpen(false)}
      >
        {validating ? (
          <div className="gv-site-validation-loading">
            <WxbSpinner tip="正在校验…" />
          </div>
        ) : validationReport ? (
          <WxbValidationList
            items={validationReport.checks.map((check) => ({
              key: check.code,
              level: check.passed ? 'success' : 'error',
              title: check.code,
              description: check.message,
            }))}
          />
        ) : null}
      </WxbModal>
    </WxbPageShell>
  );
};

export default SiteManagementPage;
